import { useRef, useEffect, useMemo } from "react";
import { useReactToPrint } from "react-to-print";
import moment from "moment";
import "./PdfOrdemPagamento.scss";
import numeroParaExtenso from "../../../util/numeroPorExtenso";

function formatCV(amount) {
  if (amount === null || amount === undefined) return "";
  // return Number(amount).toLocaleString("pt-PT", {
  //   minimumFractionDigits: 2,
  //   maximumFractionDigits: 2,
  // });
  return Number(amount);
}

const printStyles = `
    @media print {
      @page {
        size: 210mm 148mm;
         margin: 10mm 8mm;
      }
    }
  `;

function PdfOrdemPagamento({ ordemData, clinicaData, showPreviousPage }) {
  const componentRef = useRef();

  const docTitle = useMemo(() => {
    const nome = ordemData?.beneficiario?.nome || "Beneficiario";
    const numero = ordemData?.numero ? `OP-${ordemData.numero}` : "OP";
    return `${numero}-${nome}_${moment(new Date()).format("DD-MM-YYYY")}`;
  }, [ordemData]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: docTitle,
    onAfterPrint: () => showPreviousPage?.(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    handlePrint();
  }, [handlePrint]);

  function valorParaExtensoComMoeda(valor) {
    if (!valor || valor === 0) return "zero escudos";

    const parteInteira = Math.floor(valor);
    const parteDecimal = Math.round((valor - parteInteira) * 100);

    let resultado = numeroParaExtenso(parteInteira);
    resultado += parteInteira === 1 ? " escudo" : " escudos";

    if (parteDecimal > 0) {
      resultado += " e " + numeroParaExtenso(parteDecimal);
      resultado += parteDecimal === 1 ? " centavo" : " centavos";
    }

    return resultado;
  }

  const valorFmt = formatCV(ordemData?.valor);
  const valorExtenso =
    ordemData?.valorPorExtenso || valorParaExtensoComMoeda(ordemData?.valor);

  return (
    <div
      ref={componentRef}
      className={`pdf-op-wrapper ${clinicaData.configuracoes?.configuracoes?.impressaoPretoEBranco?.ativo ? " pdf-wrapper--pretoebranco" : ""}`}
    >
      <style>{printStyles}</style>
      {/* HEADER */}
      <div className="pdf-op-header">
        <div className="pdf-op-header__left">
          {clinicaData?.logotipo ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_LINK}/${clinicaData.logotipo}`}
              alt="Logotipo"
            />
          ) : (
            <div className="pdf-op-logo-placeholder">LOGO</div>
          )}
        </div>

        <div className="pdf-op-header__center">
          <div className="pdf-op-empresa-nome">
            {clinicaData?.nome || "Nome da Empresa"}
          </div>
          {clinicaData?.responsavel && (
            <div className="pdf-op-empresa-linha">
              {clinicaData.responsavel}
            </div>
          )}
          <div className="pdf-op-empresa-linha">
            Tel.: {clinicaData?.contacto || "—"}
            {clinicaData?.contacto2 ? ` / ${clinicaData.contacto2}` : ""}
          </div>
          {clinicaData?.endereco && (
            <div className="pdf-op-empresa-linha">{clinicaData.endereco}</div>
          )}
          {clinicaData?.nif && (
            <div className="pdf-op-empresa-linha">NIF: {clinicaData.nif}</div>
          )}
        </div>

        <div className="pdf-op-header__right">
          <div className="pdf-op-title-numero">
            <span className="pdf-op-title">ORDEM DE PAGAMENTO</span>
            <span className="pdf-op-numero">{ordemData?.numero || "—"}</span>
          </div>
        </div>
      </div>

      {/* CAMPOS PRINCIPAIS */}
      <div className="pdf-op-fields">
        <div className="pdf-op-field-row">
          <span className="pdf-op-label">Beneficiário:</span>
          <span className="pdf-op-line">{ordemData?.beneficiario || ""}</span>
        </div>

        <div className="pdf-op-field-row">
          <span className="pdf-op-label">Motivo de pagamento:</span>
          <span className="pdf-op-line">
            {ordemData?.motivoPagamento || ""}
          </span>
        </div>

        <div className="pdf-op-row-banco">
          <div className="pdf-op-inline-group" style={{ textAlign: "left" }}>
            <span className="pdf-op-label">Banco:</span>
            <span className="pdf-op-line">{ordemData?.banco || ""}</span>
          </div>

          <div className="pdf-op-inline-group" style={{ textAlign: "left" }}>
            <span className="pdf-op-label">CHQ N°</span>
            <span className="pdf-op-line">{ordemData?.chequeNumero || ""}</span>
          </div>

          <div
            className="pdf-op-inline-group-checkbox"
            style={{ textAlign: "center" }}
          >
            <span className="pdf-op-label">Bord</span>
            <span className="pdf-op-checkbox-box"></span>
          </div>

          <div
            className="pdf-op-inline-group-valor"
            style={{ textAlign: "right" }}
          >
            <span className="pdf-op-label">Valor</span>
            <span className="pdf-op-line-valor">{valorFmt}$00</span>
          </div>
        </div>

        <div className="pdf-op-field-row">
          <span className="pdf-op-label">Valor por extenso:</span>
          <span className="pdf-op-line">{valorExtenso}</span>
        </div>
      </div>

      {/* TABELA */}
      <div className="pdf-op-table">
        <div className="pdf-op-table__header">
          <div className="col-classificacao">Classificação</div>
          <div className="col-descricao">Descrição</div>
          <div
            className="col-debito-header"
            style={{ gridColumn: "span 4", textAlign: "center" }}
          >
            Valores Débito
          </div>
          <div
            className="col-credito-header"
            style={{ gridColumn: "span 4", textAlign: "center" }}
          >
            Valores Crédito
          </div>
        </div>

        {Array.from({ length: 5 }).map((_, idx) => {
          return (
            <div className="pdf-op-table__row" key={idx}>
              <div className="col-classificacao"></div>
              <div className="col-descricao"></div>
              <div className="col-debito-sub"></div>
              <div className="col-debito-sub"></div>
              <div className="col-debito-sub"></div>
              <div className="col-debito-sub"></div>
              <div className="col-credito-sub"></div>
              <div className="col-credito-sub"></div>
              <div className="col-credito-sub"></div>
              <div className="col-credito-sub"></div>
            </div>
          );
        })}

        {/* Linha TOTAL */}
        <div className="pdf-op-table__row">
          <div className="col-classificacao"></div>
          <div className="col-descricao"></div>
          <div className="col-debito-sub"></div>
          <div className="col-debito-sub"></div>
          <div className="col-debito-sub"></div>
          <div className="col-debito-sub"></div>
          <div className="col-credito-sub"></div>
          <div className="col-credito-sub"></div>
          <div className="col-credito-sub"></div>
          <div className="col-credito-sub"></div>
        </div>
      </div>

      {/* ASSINATURAS */}
      <div className="pdf-op-signatures">
        <div className="sig-group">
          <div className="sig-label">Elaborado por:</div>
          <div className="sig-line"></div>
        </div>

        <div className="sig-group">
          <div className="sig-label">Autorizado por:</div>
          <div className="sig-line"></div>
        </div>

        <div className="sig-group">
          <div className="sig-label">Autorizado por</div>
          <div className="sig-line"></div>
        </div>
      </div>

      {/* RECEBIDO */}
      <div className="pdf-op-recebido">
        <div className="pdf-op-recebido__content">
          <div className="recebido-data">
            <span className="recebido-label">Recebido em:</span>
            <span className="recebido-value"></span>/
            <span className="recebido-value"></span>/
            <span className="recebido-value"></span>
          </div>
          <div className="recebido-assinatura">
            <span className="recebido-hint">
              (carimbo/assinatura do beneficiário)
            </span>
          </div>
        </div>
      </div>
      <div className="pdf-op-poweredby">OdontoHT/Healthtech CV</div>
    </div>
  );
}

export default PdfOrdemPagamento;
